import { RefinanceDebt } from "@/features/refinance/types";
import { toAmountOrZero } from "@/shared/amount";

const isEmptyDraft = (debt: RefinanceDebt) =>
	!debt.bank &&
	!debt.description &&
	(debt.amount == null || debt.amount === 0) &&
	!debt.iban &&
	!debt.loanNumber;

export const isCommittedDebt = (debt: RefinanceDebt): boolean =>
	debt.internalRedemption === true ||
	(debt.isReported
		? debt.saved === true || !!debt.iban || !!debt.loanNumber
		: debt.saved === true || !isEmptyDraft(debt));

export type ReportedAmountIssue =
	{ kind: "notPositive" } | { kind: "aboveOriginal"; originalAmount: number };

/**
 * Amount rules for a debt the credit check reported. They hold whether or not
 * the debt is committed yet, but only for an amount the customer has changed:
 * the reported balance itself, which can be 0, never blocks the step.
 */
export const findReportedAmountIssue = (
	debt: RefinanceDebt
): ReportedAmountIssue | undefined => {
	if (
		!debt.isReported ||
		debt.originalAmount == null ||
		debt.amount === debt.originalAmount
	) {
		return undefined;
	}

	const amount = toAmountOrZero(debt.amount);

	if (amount <= 0) return { kind: "notPositive" };
	if (amount > debt.originalAmount) {
		return { kind: "aboveOriginal", originalAmount: debt.originalAmount };
	}

	return undefined;
};

export const sumCommittedDebts = (debts: RefinanceDebt[]): number =>
	debts.reduce(
		(total, debt) =>
			isCommittedDebt(debt) ? total + toAmountOrZero(debt.amount) : total,
		0
	);
